type CurrencyFormatOptions = {
  currency?: string;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  notation?: Intl.NumberFormatOptions['notation'];
  compactDisplay?: Intl.NumberFormatOptions['compactDisplay'];
  fallback?: string;
};

const isFiniteNumber = (value: number): boolean => Number.isFinite(value);

const parseNumberValue = (value: string): number | null => {
  const normalized = value.replace(/[^0-9.-]/g, '');
  if (!normalized || normalized === '-' || normalized === '.' || normalized === '-.') {
    return null;
  }
  const parsed = Number(normalized);
  return isFiniteNumber(parsed) ? parsed : null;
};

export const normalizeCurrencyValue = (
  value: number | string | null | undefined,
): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'number') {
    return isFiniteNumber(value) ? String(value) : '';
  }
  const parsed = parseNumberValue(value);
  return parsed === null ? '' : String(parsed);
};

export const formatCurrency = (
  value: number | string | null | undefined,
  options: CurrencyFormatOptions = {},
): string => {
  const {
    currency = 'USD',
    locale = 'en-US',
    notation,
    compactDisplay,
    fallback = '—',
  } = options;

  // Resolve fraction digits ensuring min <= max to avoid RangeError.
  // When only maximumFractionDigits is provided, clamp min to not exceed it.
  const resolvedMax = options.maximumFractionDigits ?? options.minimumFractionDigits ?? 2;
  const resolvedMin = Math.min(options.minimumFractionDigits ?? 2, resolvedMax);
  const minimumFractionDigits = resolvedMin;
  const maximumFractionDigits = resolvedMax;

  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const numeric =
    typeof value === 'number' ? value : parseNumberValue(value);

  if (numeric === null || !isFiniteNumber(numeric)) {
    return fallback;
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
    notation,
    compactDisplay,
  }).format(numeric);
};

export const normalizePhoneNumber = (
  value: string | null | undefined,
): string => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

type PhoneFormatOptions = {
  includeCountryCode?: boolean;
  fallback?: string;
};

export const formatPhoneUS = (
  value: string | null | undefined,
  options: PhoneFormatOptions = {},
): string => {
  const { includeCountryCode = false, fallback = '—' } = options;
  if (!value) return fallback;

  const digits = normalizePhoneNumber(value);
  if (!digits) return fallback;

  let local = digits;
  let hasCountry = false;

  if (digits.length === 11 && digits.startsWith('1')) {
    local = digits.slice(1);
    hasCountry = true;
  } else if (digits.length > 11 && digits.startsWith('1')) {
    local = digits.slice(-10);
    hasCountry = true;
  } else if (digits.length > 10) {
    local = digits.slice(-10);
  }

  if (local.length !== 10) {
    return digits;
  }

  const area = local.slice(0, 3);
  const prefix = local.slice(3, 6);
  const line = local.slice(6);
  const formatted = `(${area}) ${prefix}-${line}`;

  if (includeCountryCode || hasCountry) {
    return `+1 ${formatted}`;
  }

  return formatted;
};

export const formatPhoneForTel = (
  value: string | null | undefined,
): string => {
  const digits = normalizePhoneNumber(value);
  if (!digits) return '';
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  return digits.startsWith('+') ? digits : `+${digits}`;
};
